/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Get access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Saved Carts Functionality
 * Integrates with Heroku API endpoint for Draft Orders
 */

function isCustomerApproved() {
  return document.body?.dataset?.customerApproved === 'true';
}

(function() {
  'use strict';
  
  // Saved Carts API URL - must be set in theme settings (Cart / Saved Carts or API section)
  const SAVED_CARTS_API_URL = (window.theme?.savedCartsApiUrl || '').toString().trim();
  
  /**
   * Save Cart - Create saved cart from current cart items
   */
  const saveCartButton = document.querySelector('[data-save-cart-button]');
  if (saveCartButton) {
    saveCartButton.addEventListener('click', async function() {
      const button = this;
      const statusEl = document.querySelector('[data-save-cart-status]');
      const cartItemsData = document.querySelector('[data-cart-items]');
      
      if (!SAVED_CARTS_API_URL) {
        if (statusEl) {
          statusEl.style.display = 'block';
          statusEl.className = 'save-cart-status alert alert--error';
          statusEl.innerHTML = '<p>Saved Carts API is not configured. Add the API URL in theme settings.</p>';
        }
        return;
      }
      
      if (!cartItemsData) {
        showSaveCartError('Cart is empty');
        return;
      }
      
      let cartItems = [];
      try {
        cartItems = JSON.parse(cartItemsData.textContent);
      } catch (e) {
        console.error('Failed to parse cart items:', e);
        showSaveCartError('Failed to read cart items');
        return;
      }
      
      if (cartItems.length === 0) {
        showSaveCartError('Cart is empty');
        return;
      }
      
      // Get customer ID from page (should be available in Liquid)
      const customerId = window.theme?.customerId || null;
      if (!customerId) {
        showSaveCartError('Customer ID not found. Please log in.');
        return;
      }
      
      // Prompt for cart name
      const cartName = prompt('Enter a name for this saved cart (optional):');
      const finalCartName = cartName && cartName.trim() ? cartName.trim() : `Cart ${new Date().toLocaleDateString()}`;
      
      // Disable button and show loading
      button.disabled = true;
      button.textContent = 'Saving...';
      if (statusEl) {
        statusEl.style.display = 'none';
        statusEl.className = 'save-cart-status';
      }
      
      try {
        const response = await fetch(SAVED_CARTS_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': window.theme?.apiSecretKey || ''
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            customer_id: customerId,
            cart_name: finalCartName,
            items: cartItems
          })
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          showSaveCartSuccess(`Cart "${finalCartName}" saved successfully!`);
          button.textContent = 'Saved!';
          setTimeout(() => {
            button.textContent = 'Save Cart';
          }, 2000);
        } else {
          showSaveCartError(data.error || 'Failed to save cart');
        }
      } catch (error) {
        console.error('Error saving cart:', error);
        showSaveCartError('Network error. Please check if the server is available.');
      } finally {
        button.disabled = false;
      }
      
      function showSaveCartSuccess(message) {
        if (statusEl) {
          statusEl.style.display = 'block';
          statusEl.className = 'save-cart-status alert alert--success';
          statusEl.innerHTML = `<p>${message}</p>`;
        }
      }
      
      function showSaveCartError(message) {
        if (statusEl) {
          statusEl.style.display = 'block';
          statusEl.className = 'save-cart-status alert alert--error';
          statusEl.innerHTML = `<p>${message}</p>`;
        }
        button.disabled = false;
        button.textContent = 'Save Cart';
      }
    });
  }
  
  /**
   * Load Saved Carts List - Fetch and display saved carts on account page
   */
  const savedCartsContainer = document.querySelector('[data-saved-carts-container]');
  if (savedCartsContainer) {
    const customerId = window.theme?.customerId || null;
    if (!SAVED_CARTS_API_URL) {
      savedCartsContainer.innerHTML = '<p class="alert alert--error">Saved Carts is not configured. Add the API URL in theme settings.</p>';
    } else if (!customerId) {
      savedCartsContainer.innerHTML = '<p>Please log in to view saved carts.</p>';
    } else {
      function renderSavedCarts(savedCarts) {
        if (!savedCarts || savedCarts.length === 0) {
          savedCartsContainer.innerHTML = '<p>No saved carts yet.</p>';
          return;
        }
        
        // Log API response for debugging
        console.log('Saved carts API response:', savedCarts);
        
        const html = savedCarts.map(cart => {
          const date = new Date(cart.date).toLocaleDateString();
          
          // Normalize price: handle both cents (integer) and dollars (decimal/string)
          let totalPrice = cart.total;
          if (totalPrice == null || totalPrice === undefined) {
            console.warn('Cart missing total price:', cart);
            totalPrice = 0;
          } else if (typeof totalPrice === 'string') {
            // If it's a string like "$10.00" or "10.00", extract numeric value
            const numericValue = parseFloat(totalPrice.replace(/[^0-9.]/g, ''));
            // If it looks like dollars (has decimal), convert to cents
            totalPrice = numericValue < 100 && numericValue.toString().includes('.') 
              ? Math.round(numericValue * 100) 
              : Math.round(numericValue);
          } else if (typeof totalPrice === 'number') {
            // If it's a number less than 100 and has decimal places, assume it's dollars
            if (totalPrice < 100 && totalPrice % 1 !== 0) {
              totalPrice = Math.round(totalPrice * 100);
            }
            // Otherwise assume it's already in cents
            totalPrice = Math.round(totalPrice);
          }
          
          console.log(`Cart "${cart.name}": total=${cart.total}, normalized=${totalPrice}, formatted=${formatMoney(totalPrice)}`);
          
          return `
            <div class="saved-cart-item card">
              <div class="card__section">
                <div class="saved-cart-item__header">
                  <h3 class="saved-cart-item__name">${escapeHtml(cart.name)}</h3>
                  <span class="saved-cart-item__date">${date}</span>
                </div>
                <div class="saved-cart-item__details">
                  <span>${cart.item_count} item${cart.item_count !== 1 ? 's' : ''}</span>
                  <span class="saved-cart-item__total">${formatMoney(totalPrice)}</span>
                </div>
                <div class="saved-cart-item__actions">
                  <button type="button" class="button button--primary" data-restore-cart="${cart.id}">
                    Restore Cart
                  </button>
                  <button type="button" class="button button--secondary" data-delete-cart="${cart.id}">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          `;
        }).join('');
        
        savedCartsContainer.innerHTML = html;
        
        // Attach event listeners
        savedCartsContainer.querySelectorAll('[data-restore-cart]').forEach(button => {
          button.addEventListener('click', function() {
            const draftOrderId = this.getAttribute('data-restore-cart');
            restoreCart(draftOrderId);
          });
        });
        
        savedCartsContainer.querySelectorAll('[data-delete-cart]').forEach(button => {
          button.addEventListener('click', function() {
            const draftOrderId = this.getAttribute('data-delete-cart');
            if (confirm('Are you sure you want to delete this saved cart?')) {
              deleteCart(draftOrderId, renderSavedCarts);
            }
          });
        });
      }
      
      async function loadSavedCarts(customerId) {
        try {
          const response = await fetch(`${SAVED_CARTS_API_URL}/${customerId}`, {
            headers: {
              'X-API-Key': window.theme?.apiSecretKey || ''
            },
            credentials: 'same-origin'
          });
          
          const contentType = response.headers.get('Content-Type') || '';
          let body = null;
          if (contentType.indexOf('application/json') !== -1) {
            try {
              body = await response.json();
            } catch (e) {
              body = null;
            }
          }
          
          if (!response.ok) {
            const msg = (body && (body.error || body.message)) || `Server responded with ${response.status}.`;
            savedCartsContainer.innerHTML = '<p class="alert alert--error">Unable to load saved carts. ' + escapeHtml(msg) + ' Check that the Saved Carts API is running and reachable.</p>';
            return;
          }
          
          if (Array.isArray(body)) {
            renderSavedCarts(body);
          } else if (body && Array.isArray(body.carts)) {
            renderSavedCarts(body.carts);
          } else if (body && Array.isArray(body.saved_carts)) {
            renderSavedCarts(body.saved_carts);
          } else {
            renderSavedCarts(body ? (body.data || []) : []);
          }
        } catch (error) {
          console.error('Error loading saved carts:', error);
          savedCartsContainer.innerHTML = '<p class="alert alert--error">Unable to load saved carts. Network or server error. Check the console and that the API URL in theme settings is correct and the server is available.</p>';
        }
      }
      
      // Load initial list
      loadSavedCarts(customerId);
      
      // Store reference for deleteCart to use
      window.loadSavedCarts = loadSavedCarts;
      window.savedCartsCustomerId = customerId;
    }
  }
  
  /**
   * Restore Saved Cart - Add items from saved cart to current cart
   */
  async function restoreCart(draftOrderId) {
    try {
      const response = await fetch(`${SAVED_CARTS_API_URL}/${draftOrderId}/restore`, {
        headers: {
          'X-API-Key': window.theme?.apiSecretKey || ''
        },
        credentials: 'same-origin'
      });
      
      // Try to read error message from response body
      const contentType = response.headers.get('Content-Type') || '';
      let errorMessage = null;
      
      if (!response.ok) {
        if (contentType.indexOf('application/json') !== -1) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorData.description || null;
            if (errorData.errors && Array.isArray(errorData.errors)) {
              errorMessage = errorData.errors.map(e => e.message || e).join(', ');
            }
            if (!errorMessage) {
              errorMessage = `Server returned ${response.status} ${response.statusText}`;
            }
          } catch (e) {
            // Response is not valid JSON, use status text
            errorMessage = `Server responded with ${response.status}`;
          }
        } else {
          errorMessage = `Server returned ${response.status} ${response.statusText}`;
        }
        
        console.error('Failed to restore cart:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorMessage: errorMessage
        });
        alert(`Unable to restore cart: ${errorMessage || 'Failed to restore cart'}`);
        return;
      }
      
      const data = await response.json();
      
      // Validate response structure
      if (!data || !Array.isArray(data.items)) {
        alert('Unable to restore cart: Invalid response from server. Expected items array.');
        console.error('Invalid restore cart response:', data);
        return;
      }
      
      if (data.items.length === 0) {
        alert('This saved cart is empty.');
        return;
      }
      
      // Add items to cart sequentially
      let successCount = 0;
      let errorCount = 0;
      const errors = [];
      
      for (const item of data.items) {
        // Validate item structure
        if (!item.variant_id || !item.quantity) {
          errorCount++;
          errors.push(`Invalid item: missing variant_id or quantity`);
          console.warn('Invalid cart item:', item);
          continue;
        }
        
        try {
          const addResponse = await fetch(`${window.routes.cartAddUrl}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              id: item.variant_id,
              quantity: item.quantity,
              properties: item.properties || {}
            })
          });
          
          if (addResponse.ok) {
            successCount++;
            // Dispatch product:added event to update cart count
            document.documentElement.dispatchEvent(new CustomEvent('product:added', {
              bubbles: true,
              detail: {
                quantity: item.quantity,
                productAdded: true
              }
            }));
          } else {
            const errorData = await addResponse.json();
            errorCount++;
            errors.push(errorData.description || 'Unable to add item');
          }
        } catch (error) {
          errorCount++;
          errors.push('Network error');
        }
      }
      
      // Show feedback
      if (successCount > 0 && errorCount === 0) {
        alert(`${successCount} item${successCount > 1 ? 's' : ''} added to cart successfully!`);
        // Redirect to cart or refresh
        if (window.theme.cartType === 'page') {
          window.location.href = window.routes.cartUrl;
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));
        }
      } else if (successCount > 0) {
        alert(`${successCount} item${successCount > 1 ? 's' : ''} added, ${errorCount} failed: ${errors.join(', ')}`);
      } else {
        alert(`Unable to restore cart: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Error restoring cart:', error);
      const errorMsg = error.message || 'Unknown error occurred';
      alert(`Unable to restore cart: ${errorMsg}. Please check if the server is available.`);
    }
  }
  
  /**
   * Delete Saved Cart
   */
  async function deleteCart(draftOrderId) {
    try {
      const response = await fetch(`${SAVED_CARTS_API_URL}/${draftOrderId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Key': window.theme?.apiSecretKey || ''
        },
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        // Remove from DOM
        const cartItem = document.querySelector(`[data-restore-cart="${draftOrderId}"]`)?.closest('.saved-cart-item');
        if (cartItem) {
          cartItem.remove();
        }
        
        // Reload list if function is available
        if (window.loadSavedCarts && window.savedCartsCustomerId) {
          window.loadSavedCarts(window.savedCartsCustomerId);
        }
      } else {
        // Try to extract error message from response body
        let errorMessage = `Server returned ${response.status} ${response.statusText}`;
        const contentType = response.headers.get('Content-Type') || '';
        if (contentType.indexOf('application/json') !== -1) {
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorData.message || errorMessage;
          } catch (e) {
            // Response body wasn't valid JSON, use default message
          }
        }
        console.error('Failed to delete cart:', {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
          errorMessage: errorMessage
        });
        alert(`Failed to delete saved cart: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error deleting cart:', error);
      const errorMsg = error.message || 'Unknown error occurred';
      alert(`Unable to delete cart: ${errorMsg}. Please check if the server is available.`);
    }
  }
  
  // Helper functions
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  function formatMoney(cents) {
    // Use Shopify's formatMoney if available (handles currency formatting correctly)
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
    }
    // Fallback: Simple money formatting - expects price in cents
    const value = typeof cents === 'number' ? cents : parseFloat(cents) || 0;
    return '$' + (value / 100).toFixed(2);
  }
})();

/**
 * Warehouse Pro Quick Shop Functionality
 * B2B Quick Order Table with per-variant quantity inputs
 * Guard: only one system should run (avoids duplicate handlers).
 */
document.addEventListener('DOMContentLoaded', function() {
    if (window.__quickShopAddToCartBound) return;
    window.__quickShopAddToCartBound = true;

    let scrollLockY = 0;

    function lockScroll() {
      if (document.body.classList.contains('wp-scroll-lock')) return;
      scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.documentElement.classList.add('wp-scroll-lock');
      document.body.classList.add('wp-scroll-lock');
      document.body.style.top = `-${scrollLockY}px`;
    }

    // Mobile 100vh fix (used by CSS: calc(var(--wp-vh) * 100))
    function setWPVh() {
      document.documentElement.style.setProperty('--wp-vh', (window.innerHeight * 0.01) + 'px');
    }
    setWPVh();

    // --- Floating FAB detector: lifts the sticky footer so it doesn't get covered
    function setMobileFabBuffer() {
      // default buffer that clears one or two bubbles
      let bufferPx = 80;

      try {
        const viewportH = window.innerHeight || 0;
        const candidates = Array.from(document.querySelectorAll('body *'));
        for (const el of candidates) {
          const cs = window.getComputedStyle(el);
          if (cs.position !== 'fixed' || cs.display === 'none' || cs.visibility === 'hidden' || +cs.opacity === 0) continue;
          const rect = el.getBoundingClientRect();
          // Consider "bubbles" near the bottom and not full-width bars
          const nearBottom = rect.bottom > viewportH - 160;
          const bubbleLike = rect.width <= 260 && rect.height <= 260;
          if (nearBottom && bubbleLike) {
            bufferPx = Math.max(bufferPx, Math.ceil(rect.height) + 24); // bubble height + breathing room
          }
        }
      } catch (e) { /* no-op */ }

      // Clamp to a sensible range and expose to CSS
      bufferPx = Math.min(Math.max(bufferPx, 64), 160);
      document.documentElement.style.setProperty('--mqs-fab-buffer', bufferPx + 'px');
    }

    // Measure the current quick-shop footer height for sticky top fallback
    function measureMobileFooterActive() {
      const popup = document.querySelector('.wp-quick-view.active');
      if (!popup) return;
      const footer = popup.querySelector('.wp-quick-order-footer');
      if (!footer) return;
      const h = Math.ceil(footer.getBoundingClientRect().height || 0);
      document.documentElement.style.setProperty('--mqs-footer-height', (h || 96) + 'px');
    }

    // Keep viewport variables fresh
    if (window.visualViewport) {
      const updateViewport = () => {
        setWPVh();
        const isKeyboardVisible = window.visualViewport.height < window.innerHeight - 150;
        document.documentElement.classList.toggle('has-soft-keyboard', isKeyboardVisible);
        setMobileFabBuffer();
        measureMobileFooterActive();
      };
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
    }

    let _resizeDebounce = null;
    window.addEventListener('resize', () => {
      clearTimeout(_resizeDebounce);
      _resizeDebounce = setTimeout(() => { setWPVh(); setMobileFabBuffer(); measureMobileFooterActive(); }, 100);
    }, { passive: true });
    window.addEventListener('orientationchange', () => { setWPVh(); setMobileFabBuffer(); measureMobileFooterActive(); });

    // Prevent iOS double‑tap zoom for specific interactive elements
    function preventDoubleTapZoom(target) {
      if (!target) return;
      let lastTouchTime = 0;
      const handler = (e) => {
        const now = Date.now();
        if (now - lastTouchTime < 350) {
          e.preventDefault();
        }
        lastTouchTime = now;
      };
      target.addEventListener('touchend', handler, { passive: false });
      target.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
    }

    // --- Cart drawer helpers (cart-specific, never generic) ---
    function getCartDrawer() {
      const selectors = [
        '#cart-drawer',          // Warehouse variant
        '#CartDrawer',           // Common OS2.0 id
        '.cart-drawer',
        '.mini-cart',
        '[data-cart-surface="drawer"]',
        '[data-drawer="cart"]',
        '[data-drawer-id="cart"]',
        '[aria-label="Cart drawer"]'
      ];
      for (const s of selectors) {
        const el = document.querySelector(s);
        if (el) return el;
      }
      return null;
    }

    function getCartOverlay() {
      return (
        document.querySelector('.drawer__overlay') ||
        document.querySelector('.cart-drawer-overlay') ||
        document.querySelector('[data-drawer-overlay]')
      );
    }

    function isCartDrawerOpen() {
      const drawer = getCartDrawer();
      return !!(drawer && (
        drawer.classList.contains('is-active') ||
        drawer.classList.contains('open') ||
        drawer.classList.contains('active') ||
        drawer.classList.contains('drawer--is-open')
      ));
    }

    function forceRepaint() {
      document.body.style.transform = 'translateZ(0)';
      void document.body.offsetHeight;
      document.body.style.transform = '';
    }

    // Treat <=1024px as "mobile" for this behavior
    function isMobileViewport() {
      return window.matchMedia('(max-width: 1024px)').matches;
    }

    // Haptics (best-effort; harmless if unsupported)
    function vibrate(ms = 8) {
      try { navigator.vibrate?.(ms); } catch (e) {}
    }

    // Visual pressed state on any button-like element within a root container
    function attachPressedState(root) {
      root.querySelectorAll('.wp-button, .wp-quantity-selector__btn').forEach((btn) => {
        const press = (e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          btn.classList.add('is-pressed');
        };
        const release = () => btn.classList.remove('is-pressed');

        btn.addEventListener('pointerdown', press);
        ['pointerup', 'pointercancel', 'pointerleave'].forEach((t) =>
          btn.addEventListener(t, release, { passive: true })
        );
      });
    }

    // Show/hide loading state on a button (adds spinner via CSS)
    function toggleLoading(btn, isLoading) {
      if (!btn) return;
      btn.classList.toggle('is-loading', !!isLoading);
      btn.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    // Add press-and-hold auto-repeat to +/- buttons, with acceleration
    function bindStepperButton(btn, input, delta) {
      if (!btn || !input) return;
      if (btn.__boundStepper) return; btn.__boundStepper = true;

      let pressTimer = null, repeatTimer = null, suppressClick = false;

      const stepOnce = () => {
        const min = parseInt(input.min, 10) || 0;
        const max = parseInt(input.max, 10) || 999;
        const cur = parseInt(input.value, 10) || 0;
        let next = cur + delta;
        next = Math.max(min, Math.min(max, next));

        if (next !== cur) {
          input.value = String(next);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          vibrate(6);
        } else if ((delta > 0 && cur >= max) || (delta < 0 && cur <= min)) {
          const ctrl = btn.closest('.wp-quantity-selector__control');
          if (ctrl) {
            ctrl.classList.add('is-max');
            setTimeout(() => ctrl.classList.remove('is-max'), 240);
          }
          vibrate(4);
        }
      };

      const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        e.preventDefault();
        suppressClick = true;

        btn.setPointerCapture?.(e.pointerId);
        btn.classList.add('is-pressed');

        stepOnce();

        pressTimer = setTimeout(() => {
          let interval = 140;
          repeatTimer = setInterval(() => {
            stepOnce();
            if (interval > 90) interval -= 10; // accelerate until ~90ms
          }, interval);
        }, 320);
      };

      const endPress = () => {
        btn.classList.remove('is-pressed');
        clearTimeout(pressTimer);
        clearInterval(repeatTimer);
      };

      btn.addEventListener('pointerdown', onPointerDown);
      ['pointerup','pointercancel','pointerleave'].forEach((t) => {
        btn.addEventListener(t, endPress, { passive: true });
      });

      // Keyboard/AT: click triggers a single step
      btn.addEventListener('click', (e) => {
        if (suppressClick) {
          e.preventDefault();
          suppressClick = false;
          return;
        }
        stepOnce();
      });
    }

    // --- Purchase limit helpers ---
    // Short-lived cart cache (2s TTL) to avoid redundant /cart.js fetches
    let _cartCache = null;
    let _cartCacheTime = 0;
    const CART_CACHE_TTL = 2000;

    function invalidateCartCache() {
      _cartCache = null;
      _cartCacheTime = 0;
    }

    async function fetchCart() {
      const now = Date.now();
      if (_cartCache && (now - _cartCacheTime) < CART_CACHE_TTL) {
        return _cartCache;
      }
      try {
        const res = await fetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) throw new Error('Cart fetch failed');
        const data = await res.json();
        _cartCache = data;
        _cartCacheTime = Date.now();
        return data;
      } catch (e) {
        return { items: [] };
      }
    }

    function buildCartMap(cart) {
      const map = new Map();
      if (cart && Array.isArray(cart.items)) {
        for (const li of cart.items) {
          const id = (li.variant_id ?? li.id);
          if (id != null) {
            const key = String(id);
            map.set(key, (map.get(key) || 0) + (li.quantity || 0));
          }
        }
      }
      return map;
    }

    function parseLimit(val) {
      const n = parseInt(val, 10);
      return Number.isFinite(n) ? n : null;
    }

    // Create/reuse a lightweight "Added to cart" toast on mobile
    function showMobileAddToast({ message = 'Added to cart', duration = 2400 } = {}) {
      let toast = document.querySelector('.wp-cart-toast');
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'wp-cart-toast';
        toast.setAttribute('role', 'status');
        toast.setAttribute('aria-live', 'polite');
        toast.innerHTML = `
        <span class="wp-cart-toast__text"></span>
        <a class="wp-cart-toast__link" href="/cart">View cart</a>
      `;
        document.body.appendChild(toast);
      }
      const text = toast.querySelector('.wp-cart-toast__text');
      if (text) text.textContent = message;

      // Show & auto-hide
      toast.classList.add('is-visible');
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => {
        toast.classList.remove('is-visible');
      }, duration);
    }

    // Ensure no drawer/overlay remains visible (mobile UX)
    function ensureCartDrawerClosed() {
      const drawer =
        document.querySelector('#cart-drawer') ||
        document.querySelector('.cart-drawer') ||
        document.querySelector('.mini-cart') ||
        document.querySelector('[data-cart-surface="drawer"]') ||
        document.querySelector('[data-drawer="cart"]') ||
        document.querySelector('[data-drawer-id="cart"]');

      const overlay =
        document.querySelector('.drawer__overlay') ||
        document.querySelector('.cart-drawer-overlay') ||
        document.querySelector('[data-drawer-overlay]');

      if (drawer) {
        drawer.classList.remove('is-active', 'drawer--is-open', 'open', 'active');
        drawer.setAttribute('aria-hidden', 'true');
        drawer.style.removeProperty('opacity');
      }
      if (overlay) {
        overlay.classList.remove('is-visible', 'open', 'active');
        overlay.style.removeProperty('opacity');
      }
      document.body.classList.remove('overflow-hidden', 'drawer-open', 'prevent-scroll');
    }

    function unlockScroll({ force = false } = {}) {
      if (!force && (isCartDrawerOpen() || document.querySelector('.wp-quick-view.active'))) return;
      if (!document.body.classList.contains('wp-scroll-lock')) return;

      document.body.classList.remove('wp-scroll-lock');
      document.documentElement.classList.remove('wp-scroll-lock');

      const y = scrollLockY;
      document.body.style.top = '';

      requestAnimationFrame(() => {
        window.scrollTo(0, y);
        forceRepaint();
      });
    }

    // Initialize Quick Shop functionality
    initQuickShop();

    // Initialize AJAX Add to Cart for single-variant product cards
    initProductCardAddToCart();

    // Add global click handler to close cart drawer when clicking outside
    initCartDrawerAutoClose();

    function initQuickShop() {
      document.addEventListener('click', function(e) {
        const quickShopBtn = e.target.closest('.js-quickshop');
        if (!quickShopBtn) return;
        if (!isCustomerApproved()) return;

        e.preventDefault();
        const productId = quickShopBtn.getAttribute('data-product-id');
        const popup = document.getElementById('wp-quick-view-' + productId);

        if (popup) {
          showPopup(popup);
          initCloseHandlers(popup);
          initQuickOrderForm(popup);
          initImageGallery(popup); // Initialize the new image gallery
          applyMobileZoomGuards(popup);
        }
      });

      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape') {
          const activePopup = document.querySelector('.wp-quick-view.active');
          if (activePopup) hidePopup(activePopup);
        }
      });
    }

    function initImageGallery(popup) {
      if (popup.dataset.galleryInitialized) return;

      const mainImage = popup.querySelector('.js-quick-view-main-image');
      const thumbnails = popup.querySelectorAll('.wp-quick-view__thumbnail-item');

      if (!mainImage || thumbnails.length === 0) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', function() {
          mainImage.src = this.dataset.fullImageUrl;
          mainImage.alt = this.dataset.fullImageAlt;

          thumbnails.forEach(t => t.classList.remove('is-active'));
          this.classList.add('is-active');
        });
      });

      popup.dataset.galleryInitialized = 'true';
    }

    function initCartDrawerAutoClose() {
      document.addEventListener('click', function(e) {
        const cartDrawer = getCartDrawer();
        if (cartDrawer) {
          const isClickInside = cartDrawer.contains(e.target);
          const isCartToggle = e.target.closest('.header__cart-icon, .cart-icon, [data-cart-open], .drawer-toggler, .drawer__close-button');

          if (!isClickInside && !isCartToggle && (cartDrawer.classList.contains('is-active') || cartDrawer.classList.contains('open') || cartDrawer.classList.contains('active') || cartDrawer.classList.contains('drawer--is-open'))) {
            cartDrawer.classList.remove('is-active', 'open', 'active', 'drawer--is-open');
            cartDrawer.setAttribute('aria-hidden', 'true');

            const overlay = getCartOverlay();
            if (overlay) overlay.classList.remove('is-visible', 'active', 'open');
            document.body.classList.remove('overflow-hidden', 'drawer-open', 'prevent-scroll');
            unlockScroll({ force: true });
          }
        }
      });
    }

    function initCloseHandlers(popup) {
      const closeBtn = popup.querySelector('.wp-quick-view__close');
      const overlay = popup.querySelector('.wp-quick-view__overlay');

      const closeHandler = (e) => {
          e.preventDefault();
          e.stopPropagation();
          hidePopup(popup);
      };

      if (!popup.closeHandlers) popup.closeHandlers = new Map();

      if (closeBtn && !popup.closeHandlers.has(closeBtn)) {
        closeBtn.addEventListener('click', closeHandler);
        popup.closeHandlers.set(closeBtn, closeHandler);
      }
      if (overlay && !popup.closeHandlers.has(overlay)) {
        overlay.addEventListener('click', closeHandler);
        popup.closeHandlers.set(overlay, closeHandler);
      }
    }

    function showPopup(popup) {
      if (popup.parentNode !== document.body) document.body.appendChild(popup);
      popup.style.display = 'flex';
      void popup.offsetWidth;
      popup.classList.add('active');
      lockScroll();
      const currentState = history.state || {};
      if (currentState.quickView !== popup.id) {
        history.pushState({ ...currentState, quickView: popup.id }, '', window.location.href);
      }

      // Mobile metrics for sticky footer
      setMobileFabBuffer();
      measureMobileFooterActive();

      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      const firstFocusableElement = popup.querySelector(focusableElements);
      const focusableContent = popup.querySelectorAll(focusableElements);
      const lastFocusableElement = focusableContent[focusableContent.length - 1];

      document.addEventListener('keydown', trapFocus);

      function trapFocus(e) {
        let isTabPressed = e.key === 'Tab' || e.keyCode === 9;
        if (!isTabPressed || !popup.classList.contains('active')) {
          return;
        }
        if (e.shiftKey) { // shift + tab
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            e.preventDefault();
          }
        } else { // tab
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            e.preventDefault();
          }
        }
      }
      popup.trapFocus = trapFocus;
      firstFocusableElement?.focus();
    }

    function hidePopup(popup) {
      if (!popup) return;
      popup.classList.remove('active');
      unlockScroll();
      if (history.state && history.state.quickView === popup.id) {
        const nextState = { ...history.state };
        delete nextState.quickView;
        history.replaceState(Object.keys(nextState).length ? nextState : null, '', window.location.href);
      }
      if (popup.opener) {
          popup.opener.focus();
          popup.opener = null;
      }
      document.removeEventListener('keydown', popup.trapFocus);
      setTimeout(() => { popup.style.display = 'none'; }, 300);
    }

    function initQuickOrderForm(popup) {
      const form = popup.querySelector('.wp-quick-order-form');
      if (!form || form.dataset.initialized) return;

      let _totalsDebounce = null;
      const updateHandler = () => {
        clearTimeout(_totalsDebounce);
        _totalsDebounce = setTimeout(() => updateQuickOrderTotals(form), 150);
      };

      form.querySelectorAll('.js-quantity-input').forEach(input => {
          input.addEventListener('input', updateHandler);
          const rowEl = input.closest('.wp-quick-order-table__row');
          const minus = rowEl?.querySelector('.qty-minus');
          const plus  = rowEl?.querySelector('.qty-plus');
          bindStepperButton(minus, input, -1);
          bindStepperButton(plus,  input, +1);
          // Guard against iOS double‑tap zoom on inputs
          preventDoubleTapZoom(input);
      });

      // Guard against iOS double‑tap zoom on +/- buttons
      form.querySelectorAll('.qty-minus, .qty-plus').forEach(btn => preventDoubleTapZoom(btn));

      form.querySelector('.js-clear-quantities')?.addEventListener('click', (e) => {
          e.preventDefault();
          form.querySelectorAll('.js-quantity-input').forEach(input => input.value = 0);
          updateQuickOrderTotals(form);
      });

      form.addEventListener('submit', (e) => {
          e.preventDefault();
          if (!isCustomerApproved()) return;

          const items = [];
          form.querySelectorAll('.js-quantity-input').forEach(input => {
              const quantity = parseInt(input.value, 10) || 0;
              if (quantity > 0) {
                  const row = input.closest('.wp-quick-order-table__row');
                  items.push({ id: row.dataset.variantId, quantity: quantity });
              }
          });
          if (items.length > 0) performAddToCart(items, form.querySelector('.js-add-all-to-cart'));
      });

      form.dataset.initialized = 'true';
      // Give immediate pressed feedback on all relevant buttons in this modal
      attachPressedState(popup);
      updateQuickOrderTotals(form);

      // Keep limits in sync with what's already in the cart
      if (!form.dataset.limitsListener) {
        document.documentElement.addEventListener('cart:refresh', () => updateQuickOrderRowLimits(form));
        form.dataset.limitsListener = 'true';
      }
      updateQuickOrderRowLimits(form);
    }

    function applyMobileZoomGuards(popup) {
      const container = popup.querySelector('.wp-quick-view__container');
      if (container) {
        ['gesturestart', 'gesturechange', 'gestureend'].forEach(evt => {
          container.addEventListener(evt, (e) => e.preventDefault(), { passive: false });
        });
      }
      popup.querySelectorAll('.qty-minus, .qty-plus, .js-quantity-input').forEach(el => preventDoubleTapZoom(el));
    }

    function updateQuickOrderTotals(form) {
      let totalCases = 0;
      let totalPrice = 0;

      form.querySelectorAll('.wp-quick-order-table__row').forEach(row => {
          const input = row.querySelector('.js-quantity-input');
          if (!input) return;

          const max = parseInt(input.getAttribute('max'), 10);
          if (Number.isFinite(max)) {
            const raw = parseInt(input.value, 10) || 0;
            if (raw > max) input.value = String(max);
          }

          const quantity = parseInt(input.value, 10) || 0;
          const price = parseInt(row.dataset.variantPrice, 10) || 0;
          const subtotal = quantity * price;

          row.querySelector('.js-row-subtotal').innerHTML = formatMoney(subtotal);
          totalCases += quantity;
          totalPrice += subtotal;

          // Toggle row-level "Limit Reached." badge only when a purchase limit exists; never for inventory-only caps
          const limitBadge = row.querySelector('.variant-limit-badge');
          if (limitBadge) {
            const purchaseLimit = parseLimit(row.dataset.purchaseLimit);
            if (purchaseLimit != null && Number.isFinite(max) && max > 0) {
              limitBadge.textContent = 'Limit Reached.';
              limitBadge.style.display = quantity >= max ? '' : 'none';
            } else {
              limitBadge.style.display = 'none';
            }
          }
      });

      form.querySelector('.js-total-price').innerHTML = formatMoney(totalPrice);

      const caseSize = parseInt(form.dataset.caseSize, 10) || 1;
      const isCaseProduct = caseSize > 1;
      const unitLabel = isCaseProduct ? 'cases' : 'units';

      // Totals wording
      const totalCountEl = form.querySelector('.js-total-count');
      const totalUnitEl = form.querySelector('.js-total-unit');
      const totalUnitsWrapEl = form.querySelector('.js-total-units-wrap');
      const totalUnitsEl = form.querySelector('.js-total-units');
      if (totalCountEl) totalCountEl.textContent = totalCases;
      if (totalUnitEl) totalUnitEl.textContent = unitLabel;
      if (totalUnitsWrapEl && totalUnitsEl) {
        if (isCaseProduct && totalCases > 0) {
          totalUnitsWrapEl.style.display = '';
          totalUnitsEl.textContent = String(totalCases * caseSize);
        } else {
          totalUnitsWrapEl.style.display = 'none';
        }
      }

      // Mobile footer "X cases selected"
      const selectedMobileEl = form.querySelector('.js-selected-cases');
      if (selectedMobileEl) {
        const noun = isCaseProduct ? (totalCases === 1 ? 'case' : 'cases') : (totalCases === 1 ? 'unit' : 'units');
        const unitsPart = isCaseProduct && totalCases > 0 ? ` (${totalCases * caseSize} units)` : '';
        selectedMobileEl.textContent = `${totalCases} ${noun}${unitsPart}`;
      }

      // Button wording
      const addBtn = form.querySelector('.js-add-all-to-cart');
      const addBtnText = addBtn.querySelector('.wp-button__text');
      const addBtnTextMobile = addBtn.querySelector('.wp-button__text-mobile');
      if (totalCases >= 1) {
          addBtn.disabled = false;
          addBtn.classList.remove('is-subtle');
          const label = `Add ${totalCases} ${isCaseProduct ? 'Case' : 'Unit'}${totalCases > 1 ? 's' : ''} To Cart`;
          if (addBtnText) addBtnText.textContent = label;
          if (addBtnTextMobile) addBtnTextMobile.textContent = label;
      } else {
          addBtn.disabled = true;
          addBtn.classList.add('is-subtle');
          const label = `Add ${isCaseProduct ? 'Cases' : 'Units'} To Cart`;
          if (addBtnText) addBtnText.textContent = label;
          if (addBtnTextMobile) addBtnTextMobile.textContent = label;
      }

      // Clear button visibility
      const clearBtn = form.querySelector('.js-clear-quantities');
      if (clearBtn) {
        clearBtn.classList.toggle('is-visible', totalCases > 0);
      }

      // Re-measure mobile footer height in case line wraps changed it
      measureMobileFooterActive();
    }

    async function updateQuickOrderRowLimits(form) {
      const cart = await fetchCart();
      const cartMap = buildCartMap(cart);

      form.querySelectorAll('.wp-quick-order-table__row').forEach(row => {
        const variantId = String(row.dataset.variantId || '');
        const purchaseLimit = parseLimit(row.dataset.purchaseLimit);
        const input = row.querySelector('.js-quantity-input');
        const minusBtn = row.querySelector('.qty-minus');
        const plusBtn  = row.querySelector('.qty-plus');
        const limitBadge = row.querySelector('.variant-limit-badge');
        if (!input) return;

        const inventoryMax = parseInt(input.dataset.inventoryMax || input.getAttribute('max') || '9999', 10) || 9999;
        const inCart = cartMap.get(variantId) || 0;

        // Liquid uses 9999 as the sentinel when a variant is not hard-capped by inventory.
        const hasFiniteInventoryCap = Number.isFinite(inventoryMax) && inventoryMax >= 0 && inventoryMax < 9999;
        const remainingInventory = hasFiniteInventoryCap ? Math.max(0, inventoryMax - inCart) : inventoryMax;

        let remainingPurchaseLimit = null;
        let effectiveMax = remainingInventory;
        if (purchaseLimit != null) {
          remainingPurchaseLimit = Math.max(0, purchaseLimit - inCart);
          effectiveMax = Math.min(remainingInventory, remainingPurchaseLimit);
        }

        input.setAttribute('max', String(effectiveMax));

        const current = parseInt(input.value, 10) || 0;
        if (current > effectiveMax) input.value = String(effectiveMax);

        const isMaxedOut = effectiveMax <= 0;
        const isPurchaseLimitReached = (purchaseLimit != null) && (remainingPurchaseLimit !== null) && (remainingPurchaseLimit <= 0);

        row.classList.toggle('is-limit', isMaxedOut);
        if (minusBtn) minusBtn.disabled = isMaxedOut;
        if (plusBtn)  plusBtn.disabled  = isMaxedOut;
        input.disabled = isMaxedOut;

        if (limitBadge) {
          if (isPurchaseLimitReached) {
            // Wording update, no "(max x)" mention
            limitBadge.textContent = 'Limit Reached.';
            limitBadge.style.display = '';
          } else {
            limitBadge.style.display = 'none';
          }
        }
      });

      updateQuickOrderTotals(form);
    }

    function initProductCardAddToCart() {
      document.addEventListener('click', function(e) {
        const addButton = e.target.closest('.js-add-to-cart');
        if (addButton) {
          e.preventDefault();
          if (!isCustomerApproved()) return;

          const variantId = addButton.getAttribute('data-variant-id');
          const items = [{ id: variantId, quantity: 1 }];
          performAddToCart(items, addButton);
        }
      });
    }

    async function filterItemsByPurchaseLimit(items, button) {
      const cart = await fetchCart();
      const cartMap = buildCartMap(cart);
      const ctxForm = button.closest('form');

      const allowed = [];
      const rejected = [];

      for (const it of items) {
        const idStr = String(it.id);
        const requested = parseInt(it.quantity, 10) || 0;

        let limit = null;
        let variantTitle = '';

        if (ctxForm) {
          const row = ctxForm.querySelector(`.wp-quick-order-table__row[data-variant-id="${idStr}"]`);
          if (row) {
            limit = parseLimit(row.dataset.purchaseLimit);
            variantTitle = (row.querySelector('.variant-info__title')?.textContent || '').trim();
          }
        }
        if (limit == null) {
          limit = parseLimit(button?.dataset?.purchaseLimit);
        }

        if (limit == null) {
          allowed.push({ id: it.id, quantity: requested });
          continue;
        }

        const inCart = cartMap.get(idStr) || 0;
        const remaining = Math.max(0, limit - inCart);
        const addQty = Math.min(requested, remaining);

        if (addQty > 0) allowed.push({ id: it.id, quantity: addQty });
        if (addQty < requested) {
          rejected.push({ id: it.id, requested, allowed: addQty, inCart, limit, title: variantTitle });
        }
      }

      return { allowed, rejected, cart };
    }

    async function performAddToCart(items, button) {
      if (!isCustomerApproved()) return;
      if (button.dataset.cartUpdating === 'true') return;
      button.dataset.cartUpdating = 'true';

      const isQuickOrder = button.classList.contains('js-add-all-to-cart');
      const buttonTextEl = button.querySelector('.wp-button__text');
      const originalText = buttonTextEl ? buttonTextEl.textContent : button.textContent;

      button.disabled = true;
      toggleLoading(button, true);
      if (buttonTextEl) buttonTextEl.textContent = 'Adding...';
      else button.textContent = 'Adding...';

      const form = isQuickOrder ? button.closest('form') : null;
      const messageArea = form ? form.querySelector('.wp-quick-view__message-area') : null;
      if (messageArea) messageArea.innerHTML = '';

      let allowed = [];
      let rejected = [];

      try {
        ({ allowed, rejected } = await filterItemsByPurchaseLimit(items, button));

        if (allowed.length === 0) {
          if (isQuickOrder && messageArea) {
            const multi = rejected.length > 1;
            const rows = rejected.map(r => {
              const name = r.title || `#${r.id}`;
              const extra = r.inCart > 0 ? ` — already in cart: ${r.inCart}/${r.limit}` : '';
              return `<li>${name}${extra}</li>`;
            }).join('');
            messageArea.innerHTML = `
              <div class="wp-quick-view-error">
                Purchase limit reached${multi ? ' for these items' : ''}.
                <ul style="margin:6px 0 0 16px;">${rows}</ul>
              </div>`;
            measureMobileFooterActive();
            messageArea.classList.remove('is-fading');
            messageArea.classList.add('is-visible');
            clearTimeout(messageArea._hideTimer); clearTimeout(messageArea._clearTimer);
            messageArea._hideTimer = setTimeout(() => {
              messageArea.classList.add('is-fading');
              messageArea._clearTimer = setTimeout(() => {
                messageArea.classList.remove('is-visible', 'is-fading');
                messageArea.innerHTML = '';
                measureMobileFooterActive();
              }, 2000);
            }, 2000);
          } else {
            const msg = 'Limit Reached.';
            if (isMobileViewport()) {
              ensureCartDrawerClosed();
              showMobileAddToast({ message: msg });
            } else {
              if (buttonTextEl) {
                buttonTextEl.textContent = 'Limit Reached.';
                setTimeout(() => { buttonTextEl.textContent = originalText; }, 1600);
              } else {
                button.textContent = 'Limit Reached.';
                setTimeout(() => { button.textContent = originalText; }, 1600);
              }
            }
          }
          return;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: allowed })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw data;
        }

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        if (!isQuickOrder) {
          if (isMobileViewport()) {
            ensureCartDrawerClosed();
            const totalQty = allowed.reduce((n, it) => n + (it.quantity || 0), 0);
            const msg = `Added ${totalQty} item${totalQty > 1 ? 's' : ''} to cart`;
            showMobileAddToast({ message: msg });
          } else {
            setTimeout(() => openCartDrawer(), 50);
          }
        }

        if (messageArea) {
          const addedCount = allowed.reduce((sum, it) => sum + (it.quantity || 0), 0);
          const caseSizeLocal = parseInt(form.dataset.caseSize, 10) || 1;
          const isCaseProductLocal = caseSizeLocal > 1;
          const totalUnits = addedCount * caseSizeLocal;

          const successLine = isCaseProductLocal
            ? `Added ${addedCount} case${addedCount !== 1 ? 's' : ''} (${totalUnits} units)`
            : `Added ${addedCount} unit${addedCount !== 1 ? 's' : ''}`;

          let extra = '';
          if (rejected.length) {
            const itemsHtml = rejected.map(r => {
              const name = r.title || `#${r.id}`;
              const delta = r.allowed > 0 ? ` (clamped to +${r.allowed})` : '';
              const ctx = r.inCart > 0 ? ` — already in cart: ${r.inCart}/${r.limit}` : '';
              return `<li>${name}${delta}${ctx}</li>`;
            }).join('');
            extra = `<div class="wp-quick-view-error" style="margin-top:6px;">
                      Some selections were limited:
                      <ul style="margin:6px 0 0 16px;">${itemsHtml}</ul>
                     </div>`;
          }

          messageArea.innerHTML =
            `<div class="wp-quick-view-success">${successLine}</div>${extra}`;

          messageArea.classList.remove('is-fading');
          messageArea.classList.add('is-visible');
          clearTimeout(messageArea._hideTimer); clearTimeout(messageArea._clearTimer);
          messageArea._hideTimer = setTimeout(() => {
            messageArea.classList.add('is-fading');
            messageArea._clearTimer = setTimeout(() => {
              messageArea.classList.remove('is-visible', 'is-fading');
              messageArea.innerHTML = '';
              measureMobileFooterActive();
            }, 2000);
          }, 2000);
        }

        invalidateCartCache();
        if (form) updateQuickOrderRowLimits(form);

      } catch (error) {
        console.error('Error adding to cart:', error);
        if (messageArea) {
          messageArea.innerHTML = `<div class=\"wp-quick-view-error\">${error?.description || 'Error adding items to cart.'}</div>`;
          measureMobileFooterActive();
        }
      } finally {
        button.dataset.cartUpdating = 'false';
        button.disabled = false;

        if (isQuickOrder && form) {
          form.querySelectorAll('.js-quantity-input').forEach(input => input.value = 0);
          updateQuickOrderTotals(form);
        } else {
          if (buttonTextEl) buttonTextEl.textContent = originalText;
          else button.textContent = originalText;
        }
        toggleLoading(button, false);
      }
    }

    window.addEventListener('popstate', function () {
      const activePopup = document.querySelector('.wp-quick-view.active');
      if (activePopup) {
        hidePopup(activePopup);
      }
    });

    function openCartDrawer() {
      const cartDrawer = getCartDrawer();
      if (!cartDrawer) {
        // If no cart drawer exists on this page, redirect to cart page
        window.location.href = '/cart';
        return;
      }
      cartDrawer.classList.add('is-active', 'open', 'active', 'drawer--is-open');
      cartDrawer.setAttribute('aria-hidden', 'false');

      const overlay = getCartOverlay();
      if (overlay) overlay.classList.add('is-visible', 'active', 'open');
      document.body.classList.add('overflow-hidden', 'drawer-open', 'prevent-scroll');
    }

    function formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      const value = (cents / 100).toFixed(2);
      return '$' + value;
    }

    // --- Header cart summary updater ---
    async function refreshCartCountBadge() {
      try {
        // Invalidate cache so we get fresh data after cart changes
        invalidateCartCache();
        const data = await fetchCart();
        const count = Number(data.item_count) || 0;
        const totalPrice = Number(data.total_price) || 0;
        updateHeaderCartBadge(count);
        updateHeaderCartTotal(totalPrice);
      } catch (e) { /* no-op */ }
    }

    function updateHeaderCartBadge(count) {
      // Single combined selector query instead of 8 separate DOM traversals
      const allBadges = document.querySelectorAll(
        '[data-cart-count], .js-cart-count, #CartCount, .header__cart-count, .header__action-item-count, .cart-count-bubble, .cart-icon-bubble, .cart-bubble'
      );

      const seen = new Set();
      allBadges.forEach(el => {
        if (!el || seen.has(el)) return;
        seen.add(el);

        // If this is a Dawn-like bubble with two spans, update both
        if (el.classList && el.classList.contains('cart-count-bubble')) {
          const spans = el.querySelectorAll('span');
          const nextCount = String(count);
          spans.forEach(s => {
            if (s.textContent !== nextCount) s.textContent = nextCount;
          });
        } else {
          const nextCount = String(count);
          if (el.textContent !== nextCount) el.textContent = nextCount;
        }

        el.setAttribute('data-count', String(count));
        const show = count > 0;
        el.classList.toggle('is-empty', !show);
        el.setAttribute('aria-hidden', show ? 'false' : 'true');

        // If element has a visually hidden mirror, keep it in sync
        const vh = el.querySelector?.('.visually-hidden');
        if (vh) vh.textContent = String(count);
      });
    }

    function updateHeaderCartTotal(totalCents) {
      const formatted = formatMoney(Number(totalCents) || 0);
      const temp = document.createElement('div');
      temp.innerHTML = formatted;
      const normalized = (temp.textContent || temp.innerText || '').trim() || formatted;
      const totals = document.querySelectorAll('[data-header-cart-total]');
      totals.forEach(el => {
        if (!el) return;
        el.textContent = normalized;
      });
    }

    // Keep badge in sync when our code (or other parts) signal a refresh.
    // Delay so theme.js _rerender completes first and /cart.js returns settled data.
    let _badgeRefreshTimer = null;
    document.documentElement.addEventListener('cart:refresh', () => {
      clearTimeout(_badgeRefreshTimer);
      _badgeRefreshTimer = setTimeout(() => refreshCartCountBadge(), 600);
    });
    document.documentElement.addEventListener('product:added', () => {
      clearTimeout(_badgeRefreshTimer);
      _badgeRefreshTimer = setTimeout(() => refreshCartCountBadge(), 600);
    });

    // Fallback: if theme internals update only the badge, keep total synced too.
    const observedCartBadge = document.querySelector('.header__cart-count');
    if (observedCartBadge && 'MutationObserver' in window) {
      const observer = new MutationObserver(() => {
        clearTimeout(_badgeRefreshTimer);
        _badgeRefreshTimer = setTimeout(() => refreshCartCountBadge(), 250);
      });
      observer.observe(observedCartBadge, { childList: true, characterData: true, subtree: true });
    }

    refreshCartCountBadge();
  });

/**
 * Bulk Order Matrix (#200)
 * Multi-variant add to cart table on product pages
 */
(function() {
  'use strict';
  
  // Check if bulk matrix exists on page
  const bulkMatrix = document.querySelector('.mvb');
  if (!bulkMatrix) return;
  if (!isCustomerApproved()) return;
  
  const variants = document.querySelectorAll('.mvb__variants__item');
  const totalPriceEl = document.querySelector('.mvb__add-button-total');
  const qtyInputs = document.querySelectorAll('.mvb__qty-selector');
  const totalQtyEl = document.querySelector('.mvb__overal-total-value');
  const variantTitles = document.querySelectorAll('.mvb__variants .vr--title');
  const addButton = document.querySelector('.mvb__add-button');
  const matrixBody = document.querySelector('.mvb__body');
  const customerIDInput = document.querySelector('.card__section__customer-id');
  
  const variantList = {
    items: []
  };
  
  // Utility: fetch config
  function fetchConfig(type = 'json', method = 'POST') {
    return {
      method: method,
      headers: { 
        'Content-Type': 'application/json', 
        'Accept': `application/${type}` 
      }
    };
  }
  
  // Clear variant list
  function clearVariantList() {
    variantList.items = [];
  }
  
  // Reset all quantities to 0
  function resetQuantities() {
    clearVariantList();
    const inputs = document.querySelectorAll('.mvb__variants .mvb__qty-selector');
    const totals = document.querySelectorAll('.vr--item-total');
    inputs.forEach(el => el.value = 0);
    totals.forEach(el => el.innerHTML = '$0.00');
  }
  
  // Calculate total price across all rows
  function calculateTotalPrice() {
    let total = 0;
    for (let i = 0; i < variants.length; i++) {
      const priceInCents = parseInt(variants[i].querySelector('.vr--item-total').dataset.itemTotal);
      total += priceInCents;
    }
    
    if (total > 0) {
      const dollars = (total / 100).toFixed(2);
      totalPriceEl.innerHTML = '$' + dollars;
      addButton.classList.remove('disabled');
    } else {
      totalPriceEl.innerHTML = '$0.00';
      addButton.classList.add('disabled');
    }
  }
  
  // Calculate total cases across all rows
  function calculateTotalCases() {
    let totalCases = 0;
    for (let i = 0; i < qtyInputs.length; i++) {
      const cases = parseInt(qtyInputs[i].value) || 0;
      totalCases += cases;
    }
    totalQtyEl.innerHTML = `${totalCases} <span class="hide-c">cases</span>`;
  }
  
  // Build variant list for cart add
  function buildVariantList() {
    variantList.items = [];
    const items = document.querySelectorAll('.mvb__variants__item');
    
    for (let i = 0; i < items.length; i++) {
      const variantId = items[i].dataset.vrId;
      const qty = parseInt(items[i].querySelector('.mvb__qty-selector').value) || 0;
      
      if (qty > 0) {
        const item = {
          quantity: qty,
          id: variantId
        };
        
        // Add customer ID if available
        if (customerIDInput && customerIDInput.value) {
          item.properties = {
            _intuitive_cid: customerIDInput.value
          };
        }
        
        variantList.items.push(item);
      }
    }
  }
  
  // Calculate subtotal for a single row
  function calculateItemTotal(input) {
    const row = input.closest('.mvb__variants__item');
    const itemTotalEl = row.querySelector('.vr--item-total');
    const priceInCents = parseInt(row.querySelector('.vr--price').dataset.price);
    const qty = parseInt(input.value) || 0;
    const subtotalCents = priceInCents * qty;
    
    itemTotalEl.dataset.itemTotal = subtotalCents;
    
    if (subtotalCents > 0) {
      const dollars = (subtotalCents / 100).toFixed(2);
      itemTotalEl.innerHTML = '$' + dollars;
    } else {
      itemTotalEl.innerHTML = '$0.00';
    }
    
    calculateTotalPrice();
    calculateTotalCases();
    buildVariantList();
  }
  
  // Quantity decrease button handler
  window.quantityChangedDown = function(button) {
    const input = button.closest('div').querySelector('.mvb__qty-selector');
    input.value = Math.ceil(input.value);
    if (parseInt(input.value) > 0) {
      input.value--;
    }
    calculateItemTotal(input);
  };
  
  // Quantity increase button handler
  window.quantityChangedUp = function(button) {
    const input = button.closest('div').querySelector('.mvb__qty-selector');
    input.value = Math.floor(input.value);
    const currentQty = parseInt(input.value) || 0;
    const maxQty = parseInt(input.max);
    const variantId = input.closest('.mvb__variants__item').dataset.vrId;
    
    if (isNaN(maxQty)) {
      // No max limit (blank max attribute); a max of 0 is a real limit
      input.value++;
      calculateItemTotal(input);
      return;
    }
    
    // Check cart for existing quantity of this variant
    const config = fetchConfig('json', 'GET');
    config.headers['Cache-Control'] = 'no-cache';
    
    fetch('/cart.js', config)
      .then(response => response.json())
      .then(cart => {
        if (cart.status) {
          alert(cart.description);
          return;
        }
        
        const inCart = cart.items.find(item => item.id == variantId);
        const qtyInCart = inCart ? inCart.quantity : 0;
        
        if (qtyInCart + currentQty >= maxQty) {
          if (currentQty === maxQty) {
            // Animate max purchase warning
            const maxPurchaseEl = input.closest('.mvb__variants__item').querySelector('.vr--max-purchase');
            maxPurchaseEl.classList.add('animate__animated', 'animate__pulse');
            setTimeout(() => {
              maxPurchaseEl.classList.remove('animate__animated', 'animate__pulse');
            }, 600);
          }
          if (inCart) {
            alert(`The maximum amount of ${inCart.product_title} are already in your cart`);
          } else {
            alert('Maximum purchase limit reached');
          }
        } else if (currentQty < maxQty) {
          input.value++;
          calculateItemTotal(input);
        }
      })
      .catch(error => {
        console.error('Error checking cart:', error);
        // Allow increment even if cart check fails
        if (currentQty < maxQty) {
          input.value++;
          calculateItemTotal(input);
        }
      });
  };
  
  // Quantity input validation
  window.chekQty = function(input) {
    const maxQty = parseInt(input.max);
    if (!isNaN(maxQty) && parseInt(input.value) > maxQty) {
      input.value = maxQty;
    }
    calculateItemTotal(input);
  };
  
  // Variant title click - change product gallery image
  variantTitles.forEach(title => {
    title.addEventListener('click', (e) => {
      const mediaId = e.target.dataset.mediaId;
      if (mediaId) {
        const thumbnailList = document.querySelector('.product-gallery__thumbnail-list');
        if (thumbnailList) {
          const thumb = thumbnailList.querySelector(`[data-media-id="${mediaId}"]`);
          if (thumb) thumb.click();
        }
      }
    });
  });
  
  // Add to cart button click
  if (addButton) {
    addButton.addEventListener('click', function(e) {
      e.preventDefault();
      if (!isCustomerApproved()) return;

      if (variantList.items.length === 0) {
        return;
      }

      // Double-click guard: disable button during request
      addButton.disabled = true;
      matrixBody.classList.add('is-loading');
      addButton.classList.add('is-loading');

      const config = fetchConfig('json');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({ items: variantList.items });

      fetch('/cart/add.js', config)
        .then(response => response.json())
        .then(data => {
          if (data.status) {
            alert(data.description);
            return;
          }

          // Success - trigger cart refresh
          resetQuantities();
          calculateTotalCases();
          totalPriceEl.innerHTML = '$0.00';

          // Trigger cart refresh event
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));

          // Open mini cart
          const cartIcon = document.querySelector('.header__cart-icon');
          if (cartIcon) {
            setTimeout(() => {
              cartIcon.click();
            }, 300);
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          alert('Error adding items to cart. Please try again.');
        })
        .finally(() => {
          addButton.disabled = false;
          addButton.classList.remove('is-loading');
          matrixBody.classList.remove('is-loading');
        });
    });
  }
})();

/* ── Cart purchase-limit enforcement (capture phase) ──
   Clamps typed values and blocks increase-quantity clicks
   before theme.js processes them. */
document.addEventListener('change', function(e) {
  var input = e.target;
  if (!input.matches('.quantity-selector:not(.quantity-selector--product) .quantity-selector__value')) return;
  var max = parseInt(input.getAttribute('max'), 10);
  if (Number.isFinite(max)) {
    var val = parseInt(input.value, 10) || 0;
    if (val > max) input.value = String(max);
  }
}, true);

document.addEventListener('click', function(e) {
  var btn = e.target.closest('[data-action="increase-quantity"]');
  if (!btn) return;
  var selector = btn.closest('.quantity-selector');
  if (!selector) return;
  var input = selector.querySelector('.quantity-selector__value');
  if (!input) return;
  var max = parseInt(input.getAttribute('max'), 10);
  if (!Number.isFinite(max)) return;
  var newQty = parseInt(btn.getAttribute('data-quantity'), 10) || 0;
  if (newQty > max) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
}, true);

/* ── Sticky Header: Scroll-Direction Show/Hide ──
   Amazon-style: hide on scroll-down, show header on scroll-up,
   show both bars at top. Desktop only (≥1000px). */
(function stickyHeaderScrollToggle() {
  var DELTA_THRESHOLD = 15;
  var TOP_THRESHOLD = 5;
  var RESIZE_DEBOUNCE = 100;

  var section = document.querySelector('[data-section-type="header"]');
  if (!section) return;

  var wrapper = section.closest('.shopify-section__header');
  if (!wrapper) return;

  /* ── State ── */
  var AT_TOP = 'AT_TOP';
  var HIDDEN = 'HIDDEN';
  var HEADER_ONLY = 'HEADER_ONLY';

  var state = AT_TOP;
  var lastScrollY = window.scrollY;
  var accumulatedDelta = 0;
  var ticking = false;
  var enabled = false;
  var cartToggle = section.querySelector('.header__cart-toggle');
  var cartToggleObserver = null;
  var stateBeforeMiniCartOpen = null;

  /* ── Helpers ── */
  function measureHeight() {
    section.style.setProperty('--sticky-section-height', wrapper.offsetHeight + 'px');
  }

  function ensureCartToggle() {
    if (!cartToggle || !section.contains(cartToggle)) {
      cartToggle = section.querySelector('.header__cart-toggle');
    }
    return cartToggle;
  }

  function isMiniCartOpen() {
    var toggle = ensureCartToggle();
    return !!(toggle && toggle.getAttribute('aria-expanded') === 'true');
  }

  function syncMiniCartOpenState() {
    var miniCartIsOpen = isMiniCartOpen();
    section.classList.toggle('mini-cart-open', miniCartIsOpen);

    if (miniCartIsOpen) {
      if (stateBeforeMiniCartOpen === null) {
        stateBeforeMiniCartOpen = state;
      }

      if (enabled && window.scrollY > TOP_THRESHOLD) {
        applyState(HEADER_ONLY);
      }
      return;
    }

    if (stateBeforeMiniCartOpen !== null && enabled) {
      if (window.scrollY <= TOP_THRESHOLD) {
        applyState(AT_TOP);
      } else {
        applyState(stateBeforeMiniCartOpen === AT_TOP ? HEADER_ONLY : stateBeforeMiniCartOpen);
      }
    }

    stateBeforeMiniCartOpen = null;
  }

  function watchMiniCartState() {
    var toggle = ensureCartToggle();
    if (!toggle || cartToggleObserver || typeof MutationObserver === 'undefined') {
      return;
    }

    cartToggleObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        if (mutations[i].type === 'attributes' && mutations[i].attributeName === 'aria-expanded') {
          syncMiniCartOpenState();
          break;
        }
      }
    });

    cartToggleObserver.observe(toggle, { attributes: true, attributeFilter: ['aria-expanded'] });
    syncMiniCartOpenState();
  }

  function applyState(newState) {
    if (newState === HIDDEN && isMiniCartOpen()) {
      newState = HEADER_ONLY;
    }

    if (newState === state) return;
    state = newState;
    section.classList.remove('sticky--hide-all', 'sticky--header-only');
    if (state === HIDDEN) {
      section.classList.add('sticky--hide-all');
    } else if (state === HEADER_ONLY) {
      section.classList.add('sticky--header-only');
    }
  }

  /* ── Scroll handler ── */
  function onScroll() {
    ticking = false;
    var currentY = window.scrollY;
    var delta = currentY - lastScrollY;
    lastScrollY = currentY;

    /* At top of page — always show everything */
    if (currentY <= TOP_THRESHOLD) {
      accumulatedDelta = 0;
      applyState(AT_TOP);
      return;
    }

    if (isMiniCartOpen()) {
      accumulatedDelta = 0;
      applyState(HEADER_ONLY);
      return;
    }

    /* Accumulate delta; reset if direction reverses */
    if ((delta > 0 && accumulatedDelta < 0) || (delta < 0 && accumulatedDelta > 0)) {
      accumulatedDelta = 0;
    }
    accumulatedDelta += delta;

    /* Require threshold before changing state */
    if (accumulatedDelta > DELTA_THRESHOLD) {
      /* Scrolling DOWN → hide everything */
      applyState(HIDDEN);
      accumulatedDelta = 0;
    } else if (accumulatedDelta < -DELTA_THRESHOLD) {
      /* Scrolling UP → show header only */
      applyState(HEADER_ONLY);
      accumulatedDelta = 0;
    }
  }

  function requestScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(onScroll);
    }
  }

  /* ── Enable / disable based on viewport ── */
  function enable() {
    if (enabled) return;
    enabled = true;
    measureHeight();
    lastScrollY = window.scrollY;
    accumulatedDelta = 0;

    /* Set initial state based on current scroll position */
    if (window.scrollY > TOP_THRESHOLD) {
      applyState(HEADER_ONLY);
    } else {
      applyState(AT_TOP);
    }

    window.addEventListener('scroll', requestScroll, { passive: true });
  }

  function disable() {
    if (!enabled) return;
    enabled = false;
    window.removeEventListener('scroll', requestScroll);
    applyState(AT_TOP);
  }

  /* ── Viewport matching ── */
  var mql = window.matchMedia('(min-width: 1000px)');

  function handleViewport(e) {
    if (e.matches) {
      enable();
    } else {
      disable();
    }
  }

  /* Listen for changes (addEventListener preferred over deprecated addListener) */
  if (mql.addEventListener) {
    mql.addEventListener('change', handleViewport);
  } else {
    mql.addListener(handleViewport);
  }

  /* ── Resize: re-measure section height (debounced) ── */
  var resizeTimer;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      if (enabled) measureHeight();
    }, RESIZE_DEBOUNCE);
  });

  /* ── Init ── */
  watchMiniCartState();
  section.addEventListener('click', function(event) {
    if (event.target.closest('[data-action="toggle-mini-cart"]')) {
      setTimeout(syncMiniCartOpenState, 0);
    }
  });
  document.documentElement.addEventListener('product:added', function() {
    setTimeout(syncMiniCartOpenState, 0);
  });
  document.documentElement.addEventListener('cart:refresh', function() {
    setTimeout(syncMiniCartOpenState, 0);
  });

  if (mql.matches) {
    enable();
  }
})();

/* Drawer filters: stage changes and apply only on button click */
(function collectionDrawerApplyMode() {
  var root = document.documentElement;
  var drawer = document.getElementById('mobile-collection-filters');
  var observer = null;
  var snapshotHtml = null;
  var hasDraftChanges = false;
  var isApplying = false;
  var isApplyInFlight = false;

  if (!root || !drawer) return;

  function getDrawerForm() {
    return drawer.querySelector('#mobile-collection-filters-form');
  }

  function isDrawerControl(target) {
    if (!target) return false;
    if (!target.closest || !target.closest('#mobile-collection-filters-form')) return false;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement)) return false;
    if (!target.name) return false;

    return target.name === 'sort_by' || target.name === 'per_page' || target.name.indexOf('filter.') === 0;
  }

  function captureSnapshot() {
    var form = getDrawerForm();
    if (!form) return;

    snapshotHtml = form.innerHTML;
    hasDraftChanges = false;
  }

  function restoreSnapshot() {
    var form = getDrawerForm();
    if (!form || !snapshotHtml) return;

    form.innerHTML = snapshotHtml;
    hasDraftChanges = false;
  }

  function getAppliedPerPage(form) {
    if (!form) return '';

    var checkedPerPage = form.querySelector('input[name="per_page"]:checked, select[name="per_page"]');
    if (!checkedPerPage) return '';

    if (checkedPerPage instanceof HTMLSelectElement) {
      return checkedPerPage.value || '';
    }

    return checkedPerPage.value || '';
  }

  function buildApplyUrl(form) {
    var formData = new FormData(form);
    var searchParams = new URLSearchParams(formData);
    var nextUrl = new URL(window.location.href);

    searchParams.delete('per_page');
    searchParams.delete('page');
    nextUrl.search = searchParams.toString();
    nextUrl.searchParams.delete('page');

    return nextUrl;
  }

  function applyFilters(button) {
    var form = getDrawerForm();
    var targetUrl;
    var perPageValue;

    if (!form || isApplyInFlight) return;

    targetUrl = buildApplyUrl(form);
    perPageValue = getAppliedPerPage(form);

    isApplyInFlight = true;
    isApplying = true;
    button.setAttribute('disabled', 'disabled');

    function finalizeApply() {
      history.pushState({ path: targetUrl.toString() }, '', targetUrl.toString());
      window.dispatchEvent(new PopStateEvent('popstate'));
      document.dispatchEvent(new Event('collection-filter:close'));
      isApplyInFlight = false;
      button.removeAttribute('disabled');
    }

    function failApply(error) {
      console.error(error);
      isApplyInFlight = false;
      isApplying = false;
      button.removeAttribute('disabled');
    }

    if (!perPageValue) {
      finalizeApply();
      return;
    }

    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        attributes: {
          collection_products_per_page: perPageValue
        }
      })
    })
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to update collection per-page setting.');
        }

        return response.json();
      })
      .then(function() {
        finalizeApply();
      })
      .catch(function(error) {
        failApply(error);
      });
  }

  observer = new MutationObserver(function(mutations) {
    mutations.forEach(function(mutation) {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'aria-hidden') {
        return;
      }

      if (drawer.getAttribute('aria-hidden') === 'false') {
        captureSnapshot();
        return;
      }

      if (isApplying) {
        isApplying = false;
        hasDraftChanges = false;
        snapshotHtml = null;
        return;
      }

      if (hasDraftChanges) {
        restoreSnapshot();
      }
    });
  });

  observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });

  root.addEventListener('change', function(event) {
    var target = event.target;

    if (!isDrawerControl(target)) return;

    hasDraftChanges = true;

    if (target.name.indexOf('filter.') === 0) {
      event.stopImmediatePropagation();
    }
  }, true);

  root.addEventListener('click', function(event) {
    var applyButton = event.target.closest('#mobile-collection-filters [data-action="apply-filters"]');

    if (!applyButton) return;

    event.preventDefault();
    applyFilters(applyButton);
  });
})();
